import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useFormContext } from "../../../../context/houseOwnerSignupFormContext";
import { useDispatch, useSelector } from "react-redux";
import { useRegisterMutation } from "../../../../slices/usersApiSlice";
import { setCredentials } from "../../../../slices/authSlice";

// shadcn/ui
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { ScrollArea } from "../../../ui/scroll-area";
import { Card, CardContent } from "../../../ui/card";
import { Label } from "../../../ui/label";
import { Loader } from "../../../base/Loader";
import { StepFooterNav } from "../../shared/NavigationFooter";
import { USER_AGREEMENT } from "./agreement";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../ui/dialog";

const PREFERENCE_OPTIONS = [
  {
    key: "snowShoveling",
    label: "Snow Shoveling",
    img: "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/snow-shovel-2.svg",
  },
  {
    key: "lawnMowing",
    label: "Lawn Mowing",
    img: "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/mowing.svg",
  },
  {
    key: "leafRaking",
    label: "Leaf Raking",
    img: "https://i.imgur.com/3OSMtre.png",
  },
];

export function Step3({ nextStep, preStep, currentStep, totalSteps }) {
  const { formData, setFormData, setStatus } = useFormContext();
  const [serverError, setServerError] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const [register] = useRegisterMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedPreferences, setSelectedPreferences] = useState({
    snowShoveling: formData.services.includes("snowShoveling"),
    lawnMowing: formData.services.includes("lawnMowing"),
    leafRaking: formData.services.includes("leafRaking"),
  });

  const togglePreference = (preference) => {
    const newPreferences = {
      ...selectedPreferences,
      [preference]: !selectedPreferences[preference],
    };
    setSelectedPreferences(newPreferences);

    const selectedServices = Object.keys(newPreferences).filter(
      (key) => newPreferences[key]
    );

    setFormData((prevData) => ({
      ...prevData,
      services: selectedServices,
    }));
  };

  const validate = () => {
    const validationErrors = {};
    if (formData.services.length === 0) {
      validationErrors.services = "Please select at least one service";
    }
    if (!agreedToTerms) {
      validationErrors.agreedToTerms = "Please agree to the terms and conditions";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setErrorDialogOpen(true);
      return false;
    }
    return true;
  };

  const submitHandler = async (e) => {
    if (!validate()) return;

    setLoading(true);

    const data = new FormData();
    data.append("userRole", formData.userRole);
    data.append("userName", formData.userName);
    data.append("phone", formData.phone);
    data.append("address", formData.address);
    data.append("city", formData.city);
    data.append("province", formData.province);
    data.append("postalCode", formData.postalCode);
    data.append("country", formData.country);
    data.append("email", formData.email);
    if (formData.referredBy) {
      data.append("referredBy", formData.referredBy);
    }
    data.append("firstName", formData.firstName);
    data.append("lastName", formData.lastName);
    data.append("password", formData.password);
    data.append("serviceRequired", JSON.stringify(formData.services));
    if (formData.image) {
     data.append('image', formData.image);
    }
    else {
        data.append('image', null);
    }


    try {
      const res = await register({ data, hasImage: formData.image != null }).unwrap();

      if (res.error) {
        setStatus({ error: true, message: res.error });
        setServerError(res.error);
        setErrorDialogOpen(true);
        setLoading(false);
        return;
      }

      dispatch(setCredentials({ ...res }));
      setStatus({ error: false, message: "Account created successfully." });
      toast("Account has been created successfully !");
      nextStep();
    } catch (err) {
      setStatus({ error: true, message: err.message });
      setServerError(err.message);
      setErrorDialogOpen(true);
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col pb-12 mx-auto w-full max-w-4xl">
      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Error</DialogTitle>
            <DialogDescription>
              {serverError && <p className="text-red-500">{serverError}</p>}
              {errors.services && <p className="text-red-500">{errors.services}</p>}
              {errors.agreedToTerms && (
                <p className="text-red-500">{errors.agreedToTerms}</p>
              )}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="px-5 mt-5 w-full">
        <h1 className="mt-4 text-3xl font-medium text-black">
          Services Preferences
        </h1>

        <div className="mt-6">
          <h2 className="text-xl font-medium text-black">Types of Services Needed</h2>
          <p className="mt-2 text-base text-zinc-800">
            Preferences can be changed later
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4">
          {PREFERENCE_OPTIONS.map((option) => (
            <Card
              key={option.key}
              onClick={() => togglePreference(option.key)}
              className={`rounded-md md:rounded-xl cursor-pointer border-2 ${
                selectedPreferences[option.key]
                  ? "border-primary bg-primary/10"
                  : "border-neutral-300"
              }`}
            >
              <CardContent className="relative flex flex-col items-center justify-center p-3">
                <img
                  src={option.img}
                  alt={option.label}
                  className="w-[72px] h-[72px] object-contain"
                />
                <span className="mt-2 text-sm text-center sm:text-start">
                  {option.label}
                </span>
                {selectedPreferences[option.key] && (
                  <img
                    src="https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/check-mark.svg"
                    className="absolute w-6 h-6 left-2 top-2"
                    alt="Selected"
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div>
          <h2 className="mt-6 text-2xl font-medium text-black">User Agreement</h2>
          <ScrollArea className="h-80 border rounded-lg p-4 mt-2 text-sm">
            <p>
              <strong>Last Updated: {USER_AGREEMENT.lastUpdated}</strong>
            </p>
            <p className="mt-2">{USER_AGREEMENT.intro}</p>

            {USER_AGREEMENT.sections.map((section, idx) => (
              <div key={idx} className="mt-3">
                <p className="font-semibold">{section.title}</p>
                {section.body && <p className="mt-1">{section.body}</p>}
                {section.list && (
                  <ul className="list-disc ml-5 mt-1">
                    {section.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </ScrollArea>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Checkbox
            id="agree"
            checked={agreedToTerms}
            onCheckedChange={(checked) => setAgreedToTerms(checked)}
          />
          <Label htmlFor="agree" className="text-sm">
            I agree to the terms and conditions.
          </Label>
        </div>

        {/* Footer Nav */}
        <StepFooterNav
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={preStep}
          onNext={submitHandler}
          nextDisabled={loading}
          finishText="Create Account"
        />
      </div>
    </div>
  );
}
